'use client';

import { MoveHorizontal } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface BeforeAfterSliderProps {
  original: string;
  generated: string;
}

export const BeforeAfterSlider: React.FC<BeforeAfterSliderProps> = ({ 
  original, 
  generated 
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback((clientX: number) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percent = (x / rect.width) * 100;
      setSliderPosition(percent);
    }
  }, []);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) handleMove(e.clientX);
  }, [isDragging, handleMove]);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (isDragging && e.touches[0]) handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);

  const stopDragging = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('touchmove', onTouchMove);
      window.addEventListener('mouseup', stopDragging);
      window.addEventListener('touchend', stopDragging);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchend', stopDragging);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchend', stopDragging);
    };
  }, [isDragging, onMouseMove, onTouchMove, stopDragging]);

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full select-none cursor-ew-resize group bg-stone-900 rounded-xl overflow-hidden border border-stone-800"
      onMouseDown={(e) => { setIsDragging(true); handleMove(e.clientX); }}
      onTouchStart={(e) => { setIsDragging(true); if (e.touches[0]) handleMove(e.touches[0].clientX); }}
    >
      {/* Background Image (Original) */}
      {/* eslint-disable-next-line @next/next/no-img-element -- Using base64 data URLs from user uploads */}
      <img 
        src={original} 
        alt="Original" 
        draggable="false"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none" 
      />

      {/* Foreground Image (Generated) - Clipped */}
      <div 
        className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- Using base64 data URLs from AI generation */}
        <img 
          src={generated} 
          alt="Redesigned" 
          draggable="false"
          className="absolute inset-0 w-full h-full object-cover" 
        />
      </div>

      {/* Slider Handle */}
      <div 
        className="absolute top-0 bottom-0 w-0.5 bg-white/50 cursor-ew-resize z-10 backdrop-blur-sm"
        style={{ left: `${sliderPosition}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(0,0,0,0.5)] text-white transition-transform transform group-hover:scale-110">
           <MoveHorizontal size={24} />
        </div>
      </div>
      
      {/* Labels */}
      <div className="absolute bottom-6 left-6 bg-black/60 text-white/90 px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase backdrop-blur-md pointer-events-none border border-white/10">
        Original
      </div>
      <div className="absolute bottom-6 right-6 bg-gold-500/90 text-stone-950 px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase backdrop-blur-md pointer-events-none shadow-lg">
        Generated Space
      </div>
    </div>
  );
};
